'use client';

import { faAngleDown } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Menu } from '@pickleballinc/react-ui';
import clsx from 'clsx';
import { useRouter } from 'next/navigation';

import { useGetCountriesLookup, useGetStatesLookup } from '@/hooks/rankings/hooks';
import { useExportRankings } from '@/modules/apr/profile/hooks';
import { ExportRankingButton } from '@/modules/apr/rankings/ExportRankingsButton';
import LiveToggle from '@/modules/rankings/components/LiveToggle';
import { TournamentTypeButtons } from '@/modules/rankings/components/TournamentTypeButtons';
import { getNewSearchParams } from '@/modules/rankings/rankingsUtils';
import { PlayerFiltersInterface } from '@/types/ranking.types';
import { csvExportPlayersRankings } from '@/utils/actions/rankings/csvExportPlayersRankings';
import {
  getPartnerRankingsFileName,
  getWeeksInLastYear,
} from '@/utils/helpers/globalRankingsUtils';

interface PlayerFiltersProps {
  filters: PlayerFiltersInterface;
  setFilters: React.Dispatch<React.SetStateAction<PlayerFiltersInterface>>;
  canExportRankings: boolean;
}

export function PlayersFilter({ filters, setFilters, canExportRankings }: PlayerFiltersProps) {
  const router = useRouter();

  const { data: countriesData } = useGetCountriesLookup();
  const allCountriesOption = { id: '', title: 'All Countries' };
  const countriesList = [allCountriesOption, ...(countriesData?.results || [])];
  const country =
    countriesList.find((item) => String(item.id) === filters.country) ?? allCountriesOption;

  const { data: statesData } = useGetStatesLookup(String(country.id));
  const allStatesOption = { id: '', title: 'All States' };
  const statesList = [allStatesOption, ...(statesData?.results || [])];
  const state = statesList.find((item) => String(item.id) === filters.state) ?? allStatesOption;

  const weeksList = getWeeksInLastYear();
  const week = weeksList.find((item) => String(item.id) === filters.week) ?? weeksList[0];

  const { exportCsv, isExporting } = useExportRankings({
    exportFn: csvExportPlayersRankings,
    getFileName: getPartnerRankingsFileName,
  });

  // Export is hidden in LIVE mode and only available to users with export access.
  const showExport = canExportRankings && filters.live === '';

  const handleReset = () => {
    router.push('?');
  };

  const handleApply = () => {
    router.replace(`?${getNewSearchParams(filters).toString()}`);
  };

  const handleExport = () => {
    exportCsv({ params: getNewSearchParams(filters).toString() });
  };

  return (
    <>
      <TournamentTypeButtons filters={filters} />
      <div className='flex w-full items-center justify-center bg-gray-50 py-6'>
        <div className='flex w-full max-w-screen-xl flex-col items-center justify-between gap-2 px-4 lg:flex-row 2xl:px-0'>
          <div className='flex gap-4'>
            <div className='flex items-end justify-center' data-testid='rankings-live'>
              <LiveToggle filters={filters} />
            </div>
            {filters.live === '' && (
              <div className='flex flex-col items-start'>
                <label className='text-sm text-gray-700'>Country</label>
                <Menu showArrow={false} offset={5} placement='bottom-start'>
                  <Menu.Trigger asChild>
                    <Button
                      variant='secondary'
                      className={clsx(
                        '!border-1 !h-[44px] !rounded-lg !border-gray-300 !bg-white !font-normal !text-gray-500 transition-all'
                      )}
                      suffixIcon={<FontAwesomeIcon icon={faAngleDown} />}
                      data-testid='rankings-country-button'
                    >
                      {country.title}
                    </Button>
                  </Menu.Trigger>
                  <Menu.List className='h-[240px] w-fit min-w-[200px] animate-fade-in overflow-y-scroll'>
                    {countriesList.map((item) => (
                      <Menu.Item
                        key={`rankings-country-${item.id}`}
                        isActive={String(item.id) === filters.country}
                        className={clsx(
                          'mb-1 flex h-8 cursor-pointer items-center rounded-md pl-3 text-sm font-normal hover:bg-gray-100',
                          String(item.id) === filters.country
                            ? '!bg-experiment text-white'
                            : 'text-gray-900'
                        )}
                        onClick={() =>
                          setFilters({
                            ...filters,
                            country: String(item.id),
                            state: filters.country === String(item.id) ? filters.state : '',
                          })
                        }
                        data-testid={`rankings-country-option-${item.title}`}
                      >
                        {item.title}
                      </Menu.Item>
                    ))}
                  </Menu.List>
                </Menu>
              </div>
            )}

            {filters.live === '' && (
              <div className='flex flex-col items-start'>
                <label className='text-sm text-gray-700'>State</label>
                <Menu showArrow={false} offset={5}>
                  <Menu.Trigger asChild>
                    <Button
                      variant='secondary'
                      className={clsx(
                        '!border-1 !h-[44px] !rounded-lg !border-gray-300 !bg-white !font-normal !text-gray-500 transition-all'
                      )}
                      suffixIcon={<FontAwesomeIcon icon={faAngleDown} />}
                      data-testid='rankings-state-button'
                    >
                      {state.title}
                    </Button>
                  </Menu.Trigger>
                  <Menu.List className='!max-h-[240px] w-fit min-w-[200px] animate-fade-in overflow-y-scroll'>
                    {statesList.map((item) => (
                      <Menu.Item
                        key={item.id}
                        isActive={String(item.id) === filters.state}
                        className={clsx(
                          'mb-1 flex h-8 cursor-pointer items-center rounded-md pl-3 text-sm font-normal hover:bg-gray-100',
                          String(item.id) === filters.state
                            ? '!bg-experiment text-white'
                            : 'text-gray-900'
                        )}
                        onClick={() => setFilters({ ...filters, state: String(item.id) })}
                        data-testid={`rankings-state-option-${item.title}`}
                      >
                        {item.title}
                      </Menu.Item>
                    ))}
                  </Menu.List>
                </Menu>
              </div>
            )}

            {filters.live === '' ? (
              <div className='flex flex-col items-start'>
                <label className='text-sm text-gray-700'>Week</label>
                <Menu showArrow={false} offset={5}>
                  <Menu.Trigger asChild>
                    <Button
                      variant='secondary'
                      className={clsx(
                        '!border-1 !h-[44px] !rounded-lg !border-gray-300 !bg-white !font-normal !text-gray-500 transition-all'
                      )}
                      suffixIcon={<FontAwesomeIcon icon={faAngleDown} />}
                      data-testid='rankings-week-button'
                    >
                      {week?.title}
                    </Button>
                  </Menu.Trigger>
                  <Menu.List className='h-[240px] w-fit min-w-[200px] animate-fade-in overflow-y-scroll'>
                    {weeksList.map((item) => (
                      <Menu.Item
                        key={item.id}
                        isActive={String(item.id) === filters.week}
                        className={clsx(
                          'mb-1 flex h-8 cursor-pointer items-center rounded-md pl-3 text-sm font-normal hover:bg-gray-100',
                          String(item.id) === filters.week
                            ? '!bg-experiment text-white'
                            : 'text-gray-900'
                        )}
                        onClick={() => setFilters({ ...filters, week: String(item.id) })}
                        data-testid={`rankings-week-option-${item.title}`}
                      >
                        {item.title}
                      </Menu.Item>
                    ))}
                  </Menu.List>
                </Menu>
              </div>
            ) : null}
          </div>

          <div className='flex animate-fade-in gap-2 self-end'>
            {showExport ? (
              <ExportRankingButton
                onExport={handleExport}
                isExporting={isExporting}
                loaderBackgroundColor='bg-brand-700/90'
              />
            ) : null}
            <Button
              variant='secondary'
              className='bg-white'
              onClick={handleReset}
              size='lg'
              data-testid='rankings-button-reset'
            >
              Reset
            </Button>
            <Button
              variant='primary'
              className='bg-experiment'
              onClick={handleApply}
              size='lg'
              data-testid='rankings-button-apply'
            >
              Apply
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
